import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { LojaDataGrid } from "../../../common";
import type { DREGerencialView } from "../types/dre-gerencial.types";
import {
  buildDreGerencialLinhas,
  type DreLinhaVariant,
} from "../helpers/build-dre-gerencial-linhas";

type Props = {
  view: DREGerencialView | null;
};

type DreGridRow = {
  id: string;
  conta: string;
  valor: string;
  variant: DreLinhaVariant;
  valorCorErro?: boolean;
  valorCorSucesso?: boolean;
};

export default function DREGerencialTabela({ view }: Props) {
  const theme = useTheme();
  const v = view;
  if (!v) return null;

  const rows: DreGridRow[] = buildDreGerencialLinhas(v).map((linha, i) => ({
    id: `dre-${i}`,
    conta: linha.conta,
    valor: linha.valorFormatado,
    variant: linha.variant,
    valorCorErro: linha.valorCorErro,
    valorCorSucesso: linha.valorCorSucesso,
  }));

  const contaCellSx = (variant: DreLinhaVariant) => {
    switch (variant) {
      case "sub":
        return { pl: 3, fontWeight: 400, color: "text.secondary" };
      case "highlight":
      case "total":
        return { fontWeight: 700 };
      case "margem":
        return { fontWeight: 600 };
      default:
        return { fontWeight: 600 };
    }
  };

  const columns: GridColDef<DreGridRow>[] = [
    {
      field: "conta",
      headerName: "Conta",
      flex: 1,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<DreGridRow>) => (
        <Typography
          variant="body2"
          sx={{ py: 0.75, ...contaCellSx(params.row.variant) }}
        >
          {params.row.conta}
        </Typography>
      ),
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 160,
      align: "right",
      headerAlign: "right",
      sortable: false,
      renderCell: (params) => {
        const { valorCorErro, valorCorSucesso, valor, variant } = params.row;
        const color = valorCorErro
          ? theme.palette.error.main
          : valorCorSucesso && variant === "total"
            ? theme.palette.success.main
            : variant === "highlight" && !valorCorErro
              ? theme.palette.text.primary
              : undefined;
        return (
          <Typography
            variant="body2"
            sx={{
              py: 0.75,
              fontWeight:
                variant === "highlight" || variant === "total" ? 700 : 600,
              color: color ?? "text.primary",
            }}
          >
            {valor}
          </Typography>
        );
      },
    },
  ];

  return (
    <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            Estrutura da DRE
          </Typography>
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          <LojaDataGrid
            rows={rows}
            columns={columns}
            hideFooter
            disableColumnMenu
            columnHeaderHeight={44}
            rowHeight={44}
            initialState={{
              pagination: { paginationModel: { pageSize: 100, page: 0 } },
            }}
            getRowClassName={(params) => {
              const variant = params.row.variant;
              if (variant === "highlight") return "dre-row-highlight";
              return "";
            }}
            sx={{
              "& .dre-row-highlight": {
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
